'use client';
import React, { FunctionComponent, useEffect, useState } from 'react';
import ProductInfoDetail from './ProductInfoDetail';
import KsimPlot from './KsimPlot';
import Distributors from './Distributors';
import VerticalContainer from '../../common/VerticalContainer';
import { useLazyGetUniquePartQuery } from '../../../state/uniquePartSlice';
import { useLocation } from 'react-router-dom';
import {
    useLazyGetParameterValuesQuery,
    useLazyGetSupportInfoByPartNumberQuery,
} from '../../../state/querySlice';
import { getParameterFormattedValueByParameterId } from '../../../utils/ParameterRelatedFunction';
import { useLazyGetAncestorsQuery } from '../../../state/definitionSlice';
import PageContainer from '../tableComponents/PageContainer';
import { useLocale } from '../../../contexts/LocaleProvider';
import { IntlProvider } from 'react-intl';
import { LanguageMapping } from '../../../utils/LanguageMapping';
import pimcoreClient from '../../../graphql/pimcore/apollo/pimcoreClient';
import { DistributorsProvider } from '../../../graphql/pimcore/modules/contacts/contexts/DistributorsProvider';
import { GeographicRegionsProvider } from '../../../graphql/pimcore/modules/geographic/contexts/GeographicRegionsProvider';
import ProductsBreadCrumbs from '../tableComponents/ProductsBreadCrumbs';
import { DetectedUniquePart } from '../../../schemas/DetectedUniquePart';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import { DetectedDefinition } from '../../../schemas/DetectedDefinition';
import { useProductsContent } from '../../../graphql/pimcore/modules/siteContent/hooks/useProductsContent';
import LoadingOverlay from '../../common/LoadingOverlay';
import { getDefinitionDisplayName } from '../../../utils/DefinitionDisplay';
import { formatIcuString } from '../../../utils/Locale';

interface SingleProductPageProps {}

function generateAncestorUrls(
    ancestors: DetectedDefinition[],
    language: string
): { name: string; url: string }[] {
    const baseAncestor = ancestors.find((a) => !a.nameBreadcrumb.includes('->'));
    const basePath = `/products/${baseAncestor?.displayName ?? ''}`;

    return ancestors.map((ancestor) => {
        const url = ancestor.nameBreadcrumb.includes('->')
            ? `${basePath}?79=${ancestor.id}`
            : basePath;

        return {
            name: getDefinitionDisplayName(ancestor, language),
            url,
        };
    });
}

const SingleProductPage: FunctionComponent<SingleProductPageProps> = () => {
    const { locale, messages } = useLocale();
    const language = LanguageMapping.get(locale) || 'ENGLISH';
    const location = useLocation();
    const country = navigator.language.split('-')[1];
    const { loading: productContentLoading, contentTree } = useProductsContent();
    const { locale: pimCoreLocale } = useLocale();

    const basePn = location.pathname.split('/part/')[1];

    const { responsiveWidth } = useResponsive();

    const [
        fetchUniquePart,
        {
            data: DetectedUniquePart,
            isFetching: isFetchingUniquePart,
            isError: isErrorUniquePart,
            isLoading: isLoadingUniquePart,
            isUninitialized: isUninitializedUniquePart,
        },
    ] = useLazyGetUniquePartQuery();

    const [
        fetchSupportInfo,
        {
            data: SupportInfo,
            isFetching: isFetchingDatasheet,
            isError: isErrorDatasheet,
            isLoading: isLoadingDatasheet,
        },
    ] = useLazyGetSupportInfoByPartNumberQuery();

    const [
        fetchAncestors,
        { data: AncestorsData, isFetching: isFetchingAncestors, isError: isErrorAncestors },
    ] = useLazyGetAncestorsQuery();

    const [
        fetchParameterValues,
        {
            data: ParameterValuesData,
            isFetching: isFetchingParameters,
            isError: isErrorParameters,
            isLoading: isLoadingParameters,
        },
    ] = useLazyGetParameterValuesQuery();

    const [totalQuantity, setTotalQuantity] = useState<number>(0);
    const [distributorQuantity, setDistributorQuantity] = useState<number>(0);

    useEffect(() => {
        if (basePn) {
            fetchUniquePart({
                basePn: basePn,
                displayArea: 'COMPONENT_EDGE_PART_DETAIL',
            });
            fetchSupportInfo(basePn);
        }
    }, [fetchUniquePart, fetchSupportInfo, basePn]);

    useEffect(() => {
        let total = distributorQuantity;

        //I think we need to match distributors here
        // if (SupportInfo?.stock) {
        //     total += SupportInfo.stock;
        // }

        setTotalQuantity(total);
    }, [SupportInfo, distributorQuantity]);

    useEffect(() => {
        if (DetectedUniquePart?.leafDefinitionId) {
            fetchAncestors(DetectedUniquePart.leafDefinitionId);
        }
    }, [fetchAncestors, DetectedUniquePart?.leafDefinitionId]);

    useEffect(() => {
        if (AncestorsData && DetectedUniquePart) {
            const targetDefinitionIds = AncestorsData.map((ancestor) => ancestor.id);
            // 取前三個parameterID 因為是 Tree MFG Series可能比較有意義
            const sortedParameterIds = (
                DetectedUniquePart.parameterValues?.map((param) => param.parameterId) || []
            )
                .sort((a, b) => a - b)
                .slice(0, 3);

            const parameterValueIds = (DetectedUniquePart.parameterValues || [])
                .filter((param) => sortedParameterIds.includes(param.parameterId))
                .map((param) => param.id);

            fetchParameterValues({
                targetDefinitionIds,
                parameterIds: sortedParameterIds,
                parameterValueIds,
            });
        }
    }, [AncestorsData, DetectedUniquePart, fetchParameterValues]);

    // 統一 loading 判斷
    const isPageLoading =
        !DetectedUniquePart && // 只要有資料，就直接視為 false
        (productContentLoading ||
            isLoadingUniquePart ||
            isFetchingUniquePart ||
            isLoadingDatasheet ||
            isFetchingDatasheet ||
            isFetchingAncestors ||
            isLoadingParameters ||
            isFetchingParameters);

    const isKsimSupport =
        getParameterFormattedValueByParameterId(DetectedUniquePart?.parameterValues, 313) === 'Yes';

    return (
        <>
            <LoadingOverlay loading={isPageLoading} />
            <IntlProvider locale={locale} key={locale} defaultLocale="en" messages={messages}>
                <GeographicRegionsProvider apolloClient={pimcoreClient}>
                    <DistributorsProvider apolloClient={pimcoreClient}>
                        <VerticalContainer>
                            {/* 「找不到資料」只在已經發過請求、且不在 loading 才顯示 */}
                            {!isPageLoading &&
                                (isErrorUniquePart ||
                                    (!isUninitializedUniquePart && !DetectedUniquePart)) && (
                                    <div>
                                        {formatIcuString(
                                            contentTree?.product?.cantFindDataFor?.content ??
                                                'Cannot find data for {basePn}',
                                            { basePn },
                                            pimCoreLocale
                                        )}
                                    </div>
                                )}

                            {AncestorsData && (
                                <PageContainer
                                    sx={{ padding: 0 }}
                                    innerSx={{ width: responsiveWidth }}
                                    containerName={'singleProductPage'}
                                >
                                    {/*I add slice here because we only support tree and level 1 definitions*/}
                                    <ProductsBreadCrumbs
                                        breadCrumbs={generateAncestorUrls(AncestorsData, language)
                                            .reverse()
                                            .slice(0, 2)}
                                    />
                                </PageContainer>
                            )}
                            {DetectedUniquePart && SupportInfo && (
                                <>
                                    <ProductInfoDetail
                                        data={DetectedUniquePart}
                                        totalQuantity={totalQuantity}
                                        supportInfo={SupportInfo}
                                        locale={locale}
                                        contentTree={contentTree}
                                    />
                                    {
                                        <KsimPlot
                                            partNumber={
                                                DetectedUniquePart.displayPn ??
                                                DetectedUniquePart.uniquePn
                                            }
                                            contentTree={contentTree}
                                        />
                                    }
                                    <Distributors
                                        partNumber={DetectedUniquePart.uniquePn}
                                        superAlias={DetectedUniquePart.superAlias}
                                        aliases={DetectedUniquePart.aliases}
                                        setTotalQuantity={setDistributorQuantity}
                                        isKsimSupport={isKsimSupport}
                                        totalQuantity={totalQuantity}
                                        contentTree={contentTree}
                                    />
                                </>
                            )}
                        </VerticalContainer>
                    </DistributorsProvider>
                </GeographicRegionsProvider>
            </IntlProvider>
        </>
    );
};
export default SingleProductPage;
export const removeCurlyBracesContent = (str: String) => {
    return str.replace(/\{.*?\}/g, '');
};
export const whatToDisplayForAliases = (data: DetectedUniquePart) => {
    const whatOnToDisplay = data.aliases
        ?.filter((a) => a.value && a.displayable)
        .map((a) => removeCurlyBracesContent(a.value));
    data.superAlias && whatOnToDisplay?.unshift(removeCurlyBracesContent(data.uniquePn));
    return whatOnToDisplay;
};
